import { useState, useEffect, useRef } from "react";

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState("about");
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  const sections = ["about", "experience", "projects", "web", "certifications", "resume", "contact"];

  const navLabels = {
    about: "About",
    experience: "Experience",
    projects: "Projects",
    web: "Web Projects",
    certifications: "Certifications",
    resume: "Resume",
    contact: "Contact",
  };

  const projects = [
    {
      title: "SIEM Monitoring with Splunk",
      tags: ["Splunk", "Active Directory", "Windows", "VirtualBox"],
      description:
        "Set up a Splunk SIEM to monitor a Windows Active Directory environment. Built dashboards to track logins, failed attempts, account changes, and off-hours access.",
      impact: "Hands-on threat detection and log analysis in a realistic domain setup.",
      icon: "◉",
    },
    {
      title: "Network Segmentation with pfSense",
      tags: ["pfSense", "Firewall", "Networking", "VirtualBox"],
      description:
        "Configured multiple network interfaces with pfSense, set up firewall rules, and segmented traffic across a virtualized lab to control what talks to what.",
      impact: "Learned how to architect and enforce network boundaries like you'd see in a real environment.",
      icon: "⬡",
    },
    {
      title: "Penetration Testing with Kali Linux",
      tags: ["Kali Linux", "Pen Testing", "Red Team", "VirtualBox"],
      description:
        "Used Kali Linux to simulate attacks and test whether my pfSense rules actually held up. Checked segmentation, probed for gaps, and verified traffic was being blocked where it should be.",
      impact: "Connected the dots between offense and defense — breaking things to make sure they're built right.",
      icon: "△",
    },
  ];

  const webProjects = [
    {
      title: "CertDash",
      tags: ["Next.js 16", "React 19", "TypeScript", "Supabase", "Stripe", "Claude AI"],
      description: "A full-stack SaaS certification study platform for IT and cybersecurity professionals. Features 18+ certification tracks with study guides, an AI tutor powered by Claude, adaptive quizzes that target weak areas, flashcards, video libraries, audio study guides, and interactive lab simulations — all with cloud-synced progress tracking and Stripe-powered subscriptions.",
      github: "https://github.com/keithkj23/certdash",
      live: "https://certdash-git-main-keiths-projects-433e36e5.vercel.app/?x-vercel-protection-bypass=s9ekxSx3DOSoHAxhmeojW9eDfL5uIXYL",
      icon: "◆",
      highlights: ["18+ certification tracks", "AI study assistant (Claude)", "Adaptive quiz engine", "Stripe subscriptions"],
    },
    {
      title: "McCray Client Manager",
      tags: ["Next.js 14", "React 18", "Supabase", "Stripe", "Resend"],
      description: "A full-stack client lifecycle management platform built for a web design agency. Combines a public-facing marketing site, an admin dashboard for managing clients, projects, invoices, domains, and outreach campaigns, and a private client portal for tracking project progress, paying invoices, and messaging the team.",
      github: "https://github.com/keithkj23/mccray-client-manager",
      live: "https://client-manager-git-main-keiths-projects-433e36e5.vercel.app/?x-vercel-protection-bypass=JpYMVzAYF5O9vsOBtSYCFN97VqXA25VF",
      icon: "◆",
      highlights: ["Admin dashboard + client portal", "Prospect finder via Google Places", "Stripe invoicing & payments", "Automated email outreach"],
    },
    {
      title: "Insurlyy",
      tags: ["Next.js 16", "React 19", "TypeScript", "Prisma", "OpenAI", "Vapi"],
      description: "An AI-powered insurance platform that simplifies policy understanding. Upload insurance documents and get plain-English breakdowns of coverage, look up benefits through clearinghouse integrations, get procedure cost estimates, discover hidden perks, and initiate AI voice calls to your insurer — all with role-based access, multi-tenant architecture, and HIPAA-aware design.",
      github: "https://github.com/keithkj23/clearcover",
      live: "https://demo.insurlyy.com",
      icon: "◆",
      highlights: ["AI policy parsing", "Voice agent calls", "Benefits lookup via EDI", "White-label enterprise support"],
    },
    {
      title: "ACT Study Dashboard",
      tags: ["React 18", "Tailwind CSS", "Supabase", "MathJS"],
      description: "A full-featured study platform for the Enhanced ACT (2025–2026), built for a sibling's test prep. Includes flashcards, quizzes with explanations, a timed full-length test simulator, an AI study buddy chatbot with a built-in math solver, essay practice with a 40-minute timer, cloud-synced progress tracking, and a focus areas analyzer — all covering English, Math, Reading, Science, and Writing.",
      github: "https://github.com/keithkj23/act-study-dashboard",
      live: "https://project-68u61.vercel.app/",
      icon: "◆",
      highlights: ["5 study modes per section", "Full-length test simulator", "AI chatbot with math solver", "Cloud sync via Supabase"],
    },
  ];

  const experience = [
    {
      role: "Night Operations Desk Manager",
      company: "Illinois State University — Student Affairs",
      period: "Jan 2026 – Present",
      location: "Normal, IL",
      bullets: [
        "Monitor access control systems, verify credentials, and manage physical security for campus facilities",
        "Respond to security incidents following established procedures, document events, and escalate as needed",
        "Conduct routine security audits including door locks, secured areas, and policy compliance checks",
      ],
    },
    {
      role: "Intern — File System & IT Operations",
      company: "Gbar Ventures",
      period: "May 2025 – Aug 2025",
      location: "Oakbrook, IL",
      bullets: [
        "Restructured file systems implementing standardized naming conventions and access organization",
        "Identified and remediated data inconsistencies including duplicate and misfiled records",
        "Configured hardware/software, applied security updates, and performed secure data wiping on decommissioned systems",
      ],
    },
  ];

  const certifications = [
    {
      name: "CompTIA Security+",
      status: "Certified",
      icon: "✦",
      color: "#4ade80",
      link: "https://cp.certmetrics.com/comptia/en/public/verify/credential/70163ae74b12433687ef5d9432baa0d9",
    },
    {
      name: "Cisco CCNA",
      status: "Expected May 2026",
      icon: "◈",
      color: "#60a5fa",
    },
    {
      name: "CompTIA CySA+",
      status: "Expected Fall 2026",
      icon: "✦",
      color: "#f59e0b",
    },
  ];

  const skills = [
    { category: "Security", items: ["Threat Modeling", "Risk Assessment", "Incident Response", "Access Control"] },
    { category: "Networking", items: ["OSPF", "VLANs", "TCP/IP", "Subnetting", "Firewalls"] },
    { category: "Cryptography", items: ["PKI", "SSL/TLS", "Symmetric/Asymmetric Encryption", "Digital Signatures"] },
    { category: "Tools", items: ["Splunk", "Wireshark", "Kali Linux", "pfSense", "Active Directory"] },
    { category: "Programming", items: ["Python", "Bash", "PowerShell", "SQL"] },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0a0f1a",
        color: "#e2e8f0",
        fontFamily: "'DM Sans', sans-serif",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=JetBrains+Mono:wght@400;500&display=swap');

        * { margin: 0; padding: 0; box-sizing: border-box; }

        ::selection {
          background: rgba(99, 179, 237, 0.3);
          color: #fff;
        }

        .nav-link {
          position: relative;
          padding: 8px 0;
          font-size: 13px;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: #64748b;
          background: none;
          border: none;
          cursor: pointer;
          font-family: 'JetBrains Mono', monospace;
          transition: color 0.3s ease;
        }
        .nav-link:hover, .nav-link.active {
          color: #e2e8f0;
        }
        .nav-link.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1px;
          background: linear-gradient(90deg, #3b82f6, #06b6d4);
        }

        .fade-up {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .fade-up.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .project-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(51, 65, 85, 0.4);
          border-radius: 12px;
          padding: 32px;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          cursor: default;
          position: relative;
          overflow: hidden;
        }
        .project-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, #3b82f6, #06b6d4, transparent);
          opacity: 0;
          transition: opacity 0.4s ease;
        }
        .project-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }
        .project-card:hover::before {
          opacity: 1;
        }

        .tag {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 11px;
          letter-spacing: 0.5px;
          font-family: 'JetBrains Mono', monospace;
          background: rgba(59, 130, 246, 0.1);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .skill-pill {
          display: inline-block;
          padding: 6px 16px;
          border-radius: 8px;
          font-size: 13px;
          background: rgba(15, 23, 42, 0.8);
          color: #94a3b8;
          border: 1px solid rgba(51, 65, 85, 0.5);
          transition: all 0.3s ease;
        }
        .skill-pill:hover {
          color: #e2e8f0;
          border-color: rgba(59, 130, 246, 0.4);
          background: rgba(59, 130, 246, 0.08);
        }

        .section-heading {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #3b82f6;
          margin-bottom: 12px;
        }

        .grid-bg {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background-image:
            linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
          background-size: 60px 60px;
          pointer-events: none;
          z-index: 0;
        }

        .glow-orb {
          position: fixed;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          filter: blur(120px);
          opacity: 0.07;
          pointer-events: none;
          z-index: 0;
        }

        .timeline-line {
          position: absolute;
          left: 7px;
          top: 24px;
          bottom: 0;
          width: 1px;
          background: linear-gradient(180deg, rgba(59, 130, 246, 0.4), transparent);
        }

        .timeline-dot {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          border: 2px solid #3b82f6;
          background: #0a0f1a;
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }

        .cert-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(51, 65, 85, 0.4);
          border-radius: 12px;
          padding: 28px;
          transition: all 0.3s ease;
        }
        .cert-card:hover {
          border-color: rgba(59, 130, 246, 0.3);
          transform: translateY(-2px);
        }

        .contact-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 24px;
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(51, 65, 85, 0.4);
          border-radius: 10px;
          color: #94a3b8;
          text-decoration: none;
          font-size: 14px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .contact-link:hover {
          color: #e2e8f0;
          border-color: rgba(59, 130, 246, 0.3);
          background: rgba(59, 130, 246, 0.05);
        }

        .project-link-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 7px 16px;
          border-radius: 8px;
          font-size: 12px;
          font-family: 'JetBrains Mono', monospace;
          letter-spacing: 0.5px;
          text-decoration: none;
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
        }
        .project-link-btn.github {
          background: rgba(255, 255, 255, 0.06);
          color: #94a3b8;
          border: 1px solid rgba(51, 65, 85, 0.5);
        }
        .project-link-btn.github:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #e2e8f0;
          border-color: rgba(148, 163, 184, 0.4);
        }
        .project-link-btn.live {
          background: rgba(59, 130, 246, 0.12);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.25);
        }
        .project-link-btn.live:hover {
          background: rgba(59, 130, 246, 0.2);
          color: #93c5fd;
          border-color: rgba(59, 130, 246, 0.4);
        }

        .mobile-menu-btn {
          display: none;
          background: none;
          border: 1px solid rgba(51, 65, 85, 0.4);
          border-radius: 8px;
          padding: 8px 12px;
          color: #94a3b8;
          cursor: pointer;
          font-size: 18px;
        }

        @media (max-width: 768px) {
          .mobile-menu-btn { display: block; }
          .nav-desktop { display: none !important; }
          .nav-mobile {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 16px 0;
          }
          .main-content {
            padding: 24px 20px !important;
          }
          .hero-title {
            font-size: 36px !important;
          }
        }

        @media (min-width: 769px) {
          .nav-mobile { display: none !important; }
        }

        .status-pulse {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #4ade80;
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>

      {/* Background Effects */}
      <div className="grid-bg" />
      <div className="glow-orb" style={{ top: "-200px", right: "-200px", background: "#3b82f6" }} />
      <div className="glow-orb" style={{ bottom: "-200px", left: "-100px", background: "#06b6d4" }} />

      {/* Navigation */}
      <nav
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backdropFilter: "blur(20px)",
          background: "rgba(10, 15, 26, 0.85)",
          borderBottom: "1px solid rgba(51, 65, 85, 0.3)",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 40px",
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "#e2e8f0", fontWeight: 600 }}>
            KM<span style={{ color: "#3b82f6" }}>.</span>
          </div>

          <div className="nav-desktop" style={{ display: "flex", gap: 32 }}>
            {sections.map((s) => (
              <button
                key={s}
                className={`nav-link ${activeSection === s ? "active" : ""}`}
                onClick={() => {
                  setActiveSection(s);
                  setMobileMenuOpen(false);
                }}
              >
                {navLabels[s]}
              </button>
            ))}
          </div>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? "✕" : "☰"}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="nav-mobile" style={{ padding: "0 40px 16px" }}>
            {sections.map((s) => (
              <button
                key={s}
                className={`nav-link ${activeSection === s ? "active" : ""}`}
                onClick={() => {
                  setActiveSection(s);
                  setMobileMenuOpen(false);
                }}
              >
                {navLabels[s]}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="main-content" style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 40px", position: "relative", zIndex: 1 }}>
        
        {/* ABOUT SECTION */}
        {activeSection === "about" && (
          <div className={`fade-up ${isLoaded ? "visible" : ""}`}>
            <div style={{ marginBottom: 64 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
                <div className="status-pulse" />
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 12,
                    color: "#4ade80",
                    letterSpacing: 1,
                  }}
                >
                  OPEN TO OPPORTUNITIES
                </span>
              </div>

              <h1
                className="hero-title"
                style={{
                  fontSize: 56,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  marginBottom: 20,
                  background: "linear-gradient(135deg, #e2e8f0, #94a3b8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Keith Mccray
              </h1>

              <p
                style={{
                  fontSize: 20,
                  color: "#64748b",
                  maxWidth: 600,
                  lineHeight: 1.7,
                  marginBottom: 32,
                }}
              >
                Cybersecurity student at Illinois State University with hands-on experience in SIEM monitoring, network defense, and penetration testing. Building secure systems from the ground up.
              </p>

              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 48 }}>
                <span className="tag">Security+</span>
                <span className="tag">CCNA (In Progress)</span>
                <span className="tag">CySA+ (In Progress)</span>
                <span className="tag">B.S. Cybersecurity — May 2027</span>
              </div>
            </div>

            {/* Skills Grid */}
            <div>
              <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 32, color: "#e2e8f0" }}>Technical Skills</h2>

              <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
                {skills.map((group) => (
                  <div key={group.category}>
                    <p
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 12,
                        color: "#64748b",
                        marginBottom: 10,
                        letterSpacing: 0.5,
                      }}
                    >
                      {group.category}
                    </p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {group.items.map((item) => (
                        <span key={item} className="skill-pill">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* EXPERIENCE SECTION */}
        {activeSection === "experience" && (
          <div className={`fade-up ${isLoaded ? "visible" : ""}`}>
            <p className="section-heading">Career</p>
            <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 48, color: "#e2e8f0" }}>Work Experience</h2>

            <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
              {experience.map((job, i) => (
                <div key={i} style={{ display: "flex", gap: 20, position: "relative" }}>
                  <div style={{ position: "relative" }}>
                    <div className="timeline-dot" />
                    {i < experience.length - 1 && <div className="timeline-line" />}
                  </div>

                  <div style={{ flex: 1, paddingBottom: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <h3 style={{ fontSize: 18, fontWeight: 600, color: "#e2e8f0" }}>{job.role}</h3>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 12,
                          color: "#64748b",
                        }}
                      >
                        {job.period}
                      </span>
                    </div>
                    <p style={{ fontSize: 14, color: "#3b82f6", marginBottom: 16 }}>
                      {job.company} · {job.location}
                    </p>

                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {job.bullets.map((b, j) => (
                        <div key={j} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                          <span style={{ color: "#334155", marginTop: 7, fontSize: 6 }}>●</span>
                          <p style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.7 }}>{b}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Education */}
            <div style={{ marginTop: 64 }}>
              <p className="section-heading">Education</p>
              <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 32, color: "#e2e8f0" }}>Academic Background</h2>

              <div
                style={{
                  background: "rgba(15, 23, 42, 0.6)",
                  border: "1px solid rgba(51, 65, 85, 0.4)",
                  borderRadius: 12,
                  padding: 32,
                }}
              >
                <h3 style={{ fontSize: 18, fontWeight: 600, color: "#e2e8f0", marginBottom: 4 }}>
                  Bachelor of Science in Cybersecurity
                </h3>
                <p style={{ color: "#3b82f6", fontSize: 14, marginBottom: 8 }}>Illinois State University · Normal, IL</p>
                <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#64748b" }}>
                  Expected May 2027
                </p>
                <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span className="tag">Cryptography & Security</span>
                  <span className="tag">Database Systems</span>
                  <span className="tag">Network Security & Design</span>
                  <span className="tag">Information Assurance</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PROJECTS SECTION */}
        {activeSection === "projects" && (
          <div className={`fade-up ${isLoaded ? "visible" : ""}`}>
            <p className="section-heading">Home Lab</p>
            <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 16, color: "#e2e8f0" }}>Projects</h2>
            <p style={{ color: "#64748b", marginBottom: 24, maxWidth: 600, lineHeight: 1.7 }}>
              Security projects I've built and run in my home lab using VirtualBox. Everything here is hands-on — no tutorials, just real configs and real troubleshooting.
            </p>
            <div style={{ marginBottom: 40 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 14px",
                  borderRadius: 8,
                  fontSize: 12,
                  fontFamily: "'JetBrains Mono', monospace",
                  background: "rgba(148, 163, 184, 0.08)",
                  color: "#64748b",
                  border: "1px solid rgba(148, 163, 184, 0.2)",
                }}
              >
                ⬡ All projects run locally in a VirtualBox VM environment
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {projects.map((project, i) => (
                <div
                  key={i}
                  className="project-card"
                  onMouseEnter={() => setHoveredProject(i)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                    <span
                      style={{
                        fontSize: 24,
                        color: "#3b82f6",
                        opacity: hoveredProject === i ? 1 : 0.6,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      {project.icon}
                    </span>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 600, color: "#e2e8f0", marginBottom: 8 }}>{project.title}</h3>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {project.tags.map((tag) => (
                          <span key={tag} className="tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.7, marginBottom: 16 }}>{project.description}</p>

                  <div
                    style={{
                      padding: "12px 16px",
                      background: "rgba(59, 130, 246, 0.05)",
                      borderRadius: 8,
                      borderLeft: "2px solid #3b82f6",
                    }}
                  >
                    <p style={{ fontSize: 13, color: "#60a5fa", lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 600 }}>Impact:</span> {project.impact}
                    </p>
                  </div>

                  {(project.github || project.live) && (
                    <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                      {project.github && (
                        <a href={project.github} target="_blank" rel="noopener noreferrer" className="project-link-btn github">
                          ⟨/⟩ GitHub
                        </a>
                      )}
                      {project.live && (
                        <a href={project.live} target="_blank" rel="noopener noreferrer" className="project-link-btn live">
                          ↗ Live Site
                        </a>
                      )}
                    </div>
                  )}

                  {project.environment && (
                    <div style={{ marginTop: 16 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 14px",
                          borderRadius: 8,
                          fontSize: 11,
                          fontFamily: "'JetBrains Mono', monospace",
                          letterSpacing: 0.5,
                          background: "rgba(148, 163, 184, 0.08)",
                          color: "#94a3b8",
                          border: "1px solid rgba(148, 163, 184, 0.2)",
                        }}
                      >
                        ⬡ {project.environment}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div
              style={{
                marginTop: 48,
                padding: "24px 32px",
                background: "rgba(59, 130, 246, 0.05)",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: 12,
                textAlign: "center",
              }}
            >
              <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#64748b", letterSpacing: 1 }}>
                MORE PROJECTS COMING SOON
              </p>
              <p style={{ fontSize: 14, color: "#94a3b8", marginTop: 8 }}>
                Currently expanding the lab with additional security tools and attack simulations.
              </p>
            </div>
          </div>
        )}

        {/* WEB PROJECTS SECTION */}
        {activeSection === "web" && (
          <div className={`fade-up ${isLoaded ? "visible" : ""}`}>
            <p className="section-heading">Development</p>
            <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 16, color: "#e2e8f0" }}>Web Projects</h2>
            <p style={{ color: "#64748b", marginBottom: 48, maxWidth: 600, lineHeight: 1.7 }}>
              Websites and web applications I've designed and built. Each project links to the live deployment and source code.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 24 }}>
              {webProjects.map((project, i) => (
                <div
                  key={i}
                  className="project-card"
                  onMouseEnter={() => setHoveredProject(`web-${i}`)}
                  onMouseLeave={() => setHoveredProject(null)}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 16 }}>
                    <span
                      style={{
                        fontSize: 24,
                        color: "#06b6d4",
                        opacity: hoveredProject === `web-${i}` ? 1 : 0.6,
                        transition: "opacity 0.3s ease",
                      }}
                    >
                      {project.icon}
                    </span>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 18, fontWeight: 600, color: "#e2e8f0", marginBottom: 8 }}>{project.title}</h3>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {project.tags.map((tag) => (
                          <span key={tag} className="tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.7, marginBottom: 20 }}>{project.description}</p>

                  {project.highlights && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
                      {project.highlights.map((h) => (
                        <span
                          key={h}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 10px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontFamily: "'JetBrains Mono', monospace",
                            background: "rgba(6, 182, 212, 0.08)",
                            color: "#22d3ee",
                            border: "1px solid rgba(6, 182, 212, 0.2)",
                          }}
                        >
                          ✓ {h}
                        </span>
                      ))}
                    </div>
                  )}

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {project.github && (
                      <a href={project.github} target="_blank" rel="noopener noreferrer" className="project-link-btn github">
                        ⟨/⟩ GitHub
                      </a>
                    )}
                    {project.live && (
                      <a href={project.live} target="_blank" rel="noopener noreferrer" className="project-link-btn live">
                        ↗ Live Site
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}

        {/* CERTIFICATIONS SECTION */}
        {activeSection === "certifications" && (
          <div className={`fade-up ${isLoaded ? "visible" : ""}`}>
            <p className="section-heading">Credentials</p>
            <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 48, color: "#e2e8f0" }}>Certifications</h2>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
              {certifications.map((cert, i) => {
                const Wrapper = cert.link ? "a" : "div";
                const wrapperProps = cert.link
                  ? { href: cert.link, target: "_blank", rel: "noopener noreferrer", style: { textDecoration: "none", color: "inherit" } }
                  : {};
                return (
                  <Wrapper key={i} {...wrapperProps}>
                    <div className="cert-card" style={cert.link ? { cursor: "pointer" } : {}}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: cert.link ? 12 : 0 }}>
                        <span style={{ fontSize: 24, color: cert.color }}>{cert.icon}</span>
                        <div>
                          <h3 style={{ fontSize: 17, fontWeight: 600, color: "#e2e8f0" }}>{cert.name}</h3>
                          <p
                            style={{
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: 11,
                              color: cert.color,
                              marginTop: 4,
                            }}
                          >
                            {cert.status}
                          </p>
                        </div>
                      </div>
                      {cert.link && (
                        <p style={{ fontSize: 11, color: "#64748b", fontFamily: "'JetBrains Mono', monospace" }}>
                          ↗ View Credential
                        </p>
                      )}
                    </div>
                  </Wrapper>
                );
              })}
            </div>
          </div>
        )}

        {/* RESUME SECTION */}
        {activeSection === "resume" && (
          <div className={`fade-up ${isLoaded ? "visible" : ""}`}>
            <p className="section-heading">Document</p>
            <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 16, color: "#e2e8f0" }}>Resume</h2>
            <p style={{ color: "#64748b", marginBottom: 48, maxWidth: 600, lineHeight: 1.7 }}>
              Download or view my current resume below.
            </p>

            <div
              style={{
                background: "rgba(15, 23, 42, 0.6)",
                border: "1px solid rgba(51, 65, 85, 0.4)",
                borderRadius: 16,
                padding: 40,
                maxWidth: 600,
              }}
            >
              {/* Resume Icon */}
              <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 32 }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 12,
                    background: "linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(6, 182, 212, 0.15))",
                    border: "1px solid rgba(59, 130, 246, 0.25)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 28,
                  }}
                >
                  📄
                </div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: "#e2e8f0", marginBottom: 4 }}>
                    Keith Mccray — Resume
                  </h3>
                  <p style={{ fontSize: 13, color: "#64748b", fontFamily: "'JetBrains Mono', monospace" }}>
                    PDF • Updated 2026
                  </p>
                </div>
              </div>

              {/* Key Highlights */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 32 }}>
                {[
                  "Security+ Certified",
                  "CCNA (In Progress)",
                  "CySA+ (In Progress)",
                  "Splunk SIEM",
                  "pfSense",
                  "Kali Linux",
                  "Python",
                  "Active Directory",
                ].map((item) => (
                  <span
                    key={item}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 100,
                      fontSize: 11,
                      fontFamily: "'JetBrains Mono', monospace",
                      background: "rgba(59, 130, 246, 0.1)",
                      color: "#60a5fa",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                    }}
                  >
                    {item}
                  </span>
                ))}
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <a
                  href="/KeithMccray_Resume.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link-btn live"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px 24px",
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: 10,
                    textDecoration: "none",
                  }}
                >
                  ↗ View in Browser
                </a>
                <a
                  href="/KeithMccray_Resume.pdf"
                  download
                  className="project-link-btn github"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "12px 24px",
                    fontSize: 14,
                    fontWeight: 600,
                    borderRadius: 10,
                    textDecoration: "none",
                  }}
                >
                  ↓ Download PDF
                </a>
              </div>

              <p style={{ fontSize: 11, color: "#475569", marginTop: 16, fontFamily: "'JetBrains Mono', monospace" }}>
                Also available upon request via email.
              </p>
            </div>

            {/* Areas of Study */}
            <div style={{ marginTop: 64 }}>
              <p className="section-heading">Knowledge Areas</p>
              <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 32, color: "#e2e8f0" }}>
                Areas of Study
              </h2>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 }}>
                {[
                  { area: "Cryptography & Security", details: "Symmetric/asymmetric encryption, digital signatures, hash functions, SSL/TLS, PKI" },
                  { area: "Database Systems", details: "SQL Oracle, data modeling, integrity constraints, normalization" },
                  { area: "Information Security", details: "Assurance principles, risk assessment, threat modeling, security controls" },
                  { area: "Network Security & Design", details: "OSPF, VLANs, TCP/IP, subnetting, segmentation" },
                ].map((item, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "20px 24px",
                      background: "rgba(15, 23, 42, 0.6)",
                      border: "1px solid rgba(51, 65, 85, 0.4)",
                      borderRadius: 10,
                    }}
                  >
                    <h4 style={{ fontSize: 14, fontWeight: 600, color: "#e2e8f0", marginBottom: 8 }}>{item.area}</h4>
                    <p style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>{item.details}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CONTACT SECTION */}
        {activeSection === "contact" && (
          <div className={`fade-up ${isLoaded ? "visible" : ""}`}>
            <p className="section-heading">Get In Touch</p>
            <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 16, color: "#e2e8f0" }}>Contact</h2>
            <p style={{ color: "#64748b", marginBottom: 48, maxWidth: 500, lineHeight: 1.7 }}>
              Open to cybersecurity internships, entry-level positions, and collaborative projects. Let's connect.
            </p>

            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 440 }}>
              <div className="contact-link">
                <span style={{ fontSize: 18 }}>✉</span>
                <div>
                  <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>EMAIL</p>
                  <p>keithkj5@yahoo.com</p>
                </div>
              </div>

              <div className="contact-link">
                <span style={{ fontSize: 18 }}>☎</span>
                <div>
                  <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>PHONE</p>
                  <p>(815) 931-5597</p>
                </div>
              </div>

              <div className="contact-link">
                <span style={{ fontSize: 18 }}>⬡</span>
                <div>
                  <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>LINKEDIN</p>
                  <p>linkedin.com/in/keith--mccray</p>
                </div>
              </div>

              <div className="contact-link">
                <span style={{ fontSize: 18 }}>◈</span>
                <div>
                  <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>GITHUB</p>
                  <p>github.com/keithkj23</p>
                </div>
              </div>

              <div className="contact-link">
                <span style={{ fontSize: 18 }}>◉</span>
                <div>
                  <p style={{ fontSize: 11, color: "#64748b", marginBottom: 2, fontFamily: "'JetBrains Mono', monospace", letterSpacing: 1 }}>PORTFOLIO</p>
                  <p>keithmccray.dev</p>
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 48,
                padding: "20px 24px",
                background: "rgba(15, 23, 42, 0.6)",
                border: "1px solid rgba(51, 65, 85, 0.4)",
                borderRadius: 10,
                maxWidth: 440,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 14 }}>📍</span>
                <p style={{ fontSize: 14, color: "#94a3b8" }}>Romeoville, IL</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14 }}>🎓</span>
                <p style={{ fontSize: 14, color: "#94a3b8" }}>Illinois State University — Class of 2027</p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer
        style={{
          position: "relative",
          zIndex: 1,
          borderTop: "1px solid rgba(51, 65, 85, 0.3)",
          padding: "24px 40px",
          textAlign: "center",
        }}
      >
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#334155", letterSpacing: 1 }}>
          © 2026 KEITH MCCRAY — BUILT WITH REACT
        </p>
      </footer>
    </div>
  );
};

export default Portfolio;
